import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Lock, Mail, ArrowRight, Crown, AlertCircle } from "lucide-react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/admin/AuthContext";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.id) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? formatApiErrorDetail(err.response?.data?.detail) || err.message
        : String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-5"
      style={{
        background:
          "radial-gradient(120% 80% at 80% 0%, rgba(212,175,55,0.10), transparent 55%), radial-gradient(80% 60% at 10% 100%, rgba(0,212,255,0.08), transparent 60%), #08080C",
      }}
    >
      {/* ambient grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #D4AF37 1px, transparent 1.5px)",
          backgroundSize: "70px 70px",
        }}
      />

      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-md"
        data-testid="admin-login-form"
      >
        <div
          className="rounded-3xl p-10 sm:p-12"
          style={{
            background:
              "linear-gradient(180deg, rgba(22,22,29,0.92), rgba(11,11,15,0.96))",
            border: "1px solid rgba(212,175,55,0.18)",
            boxShadow:
              "0 30px 80px -20px rgba(0,0,0,0.7), inset 1px 0 0 rgba(255,255,255,0.04)",
          }}
        >
          <div className="flex flex-col items-center mb-9">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
              style={{
                background:
                  "linear-gradient(180deg, rgba(212,175,55,0.18), rgba(212,175,55,0.04))",
                border: "1px solid rgba(212,175,55,0.42)",
                boxShadow: "0 0 30px -6px rgba(212,175,55,0.6)",
              }}
            >
              <Crown size={22} className="text-[#D4AF37]" strokeWidth={1.6} />
            </div>
            <p className="font-mono-pl text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] mb-2">
              Admin Console
            </p>
            <h1 className="font-display text-3xl sm:text-4xl uppercase text-[#F5F5F5] tracking-wide text-center leading-none">
              PURELIFE <span className="text-gold glow-gold">CMS</span>
            </h1>
            <p className="mt-3 text-sm text-[#B6B6BE] text-center">
              Accesso riservato al team.
            </p>
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="mb-5 flex items-start gap-2.5 rounded-xl px-4 py-3 text-sm"
              style={{
                background: "rgba(220,38,38,0.12)",
                border: "1px solid rgba(220,38,38,0.45)",
                color: "#FFB5B5",
              }}
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0" strokeWidth={2.2} />
              <span>{error}</span>
            </div>
          )}

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.28em] text-[#B6B6BE] mb-2">
              Email
            </span>
            <div className="relative">
              <Mail
                size={15}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#00D4FF]"
                strokeWidth={2.2}
              />
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                placeholder="admin@purelife.it"
                className="search-input w-full pl-10 pr-3 py-3 rounded-xl bg-[#0B0B0F]/90 border border-[#D4AF37]/30 text-sm text-[#F5F5F5] placeholder:text-[#B6B6BE]/55 focus:outline-none"
              />
            </div>
          </label>

          <label className="block mt-5">
            <span className="block text-[10px] uppercase tracking-[0.28em] text-[#B6B6BE] mb-2">
              Password
            </span>
            <div className="relative">
              <Lock
                size={15}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#00D4FF]"
                strokeWidth={2.2}
              />
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                placeholder="••••••••••"
                className="search-input w-full pl-10 pr-3 py-3 rounded-xl bg-[#0B0B0F]/90 border border-[#D4AF37]/30 text-sm text-[#F5F5F5] placeholder:text-[#B6B6BE]/55 focus:outline-none"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-gold mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold uppercase tracking-wider disabled:opacity-60"
          >
            {loading ? "Verifica…" : "Entra nel pannello"}
            <ArrowRight size={15} strokeWidth={2.6} />
          </button>

          <p className="mt-8 text-center text-[10px] uppercase tracking-[0.32em] text-[#B6B6BE]/55">
            MMXXVI · Codice della Città
          </p>
        </div>
      </form>
    </div>
  );
}
